import React, { useEffect, useState } from "react";
import { Config, Endpoint } from "../../../model_gen/config";
import { IntentService } from "../../../services/IntentService";
import { ConfigService } from "../../../services/ConfigService";
import { usePermissions } from "../../../PermissionsProvider";
import ConfigurationPanel from "../../configuration/ConfigurationPanel";
import { IntentGroup } from "../../../model_gen/evaluation";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Skeleton } from "../../ui/skeleton";
import { SettingsIcon } from "lucide-react";


interface TestRunConfigProps {
  intentGroupId: number;
  testSetId: number;
  config?: Config;
  onChange: (config: Config) => void;
  onEndpointChange: (endpoint: string) => void;
}

const TestRunConfig: React.FC<TestRunConfigProps> = ({
  intentGroupId,
  config: initialConfig,
  onChange,
  onEndpointChange,
}) => {
  const { apiKey } = usePermissions();
  const [isLoading, setIsLoading] = useState(true);
  const [config, setConfig] = useState<Config | undefined>(initialConfig);
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  const [selectedEndpoint, setSelectedEndpoint] = useState<string>("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    // Fetch endpoints
    ConfigService.getEndpoints(setIsLoading, apiKey).subscribe(
      (fetchedEndpoints: Endpoint[]) => {
        setEndpoints(fetchedEndpoints);
        const defaultEndpoint = fetchedEndpoints.find((e) => e.default);
        if (defaultEndpoint) {
          setSelectedEndpoint(defaultEndpoint.address || "");
          onEndpointChange(defaultEndpoint.address || "");
        } else if (fetchedEndpoints.length > 0) {
          setSelectedEndpoint(fetchedEndpoints[0].address || "");
          onEndpointChange(fetchedEndpoints[0].address || "");
        }
      },
      (error: any) => {
        console.error(error);
        setIsLoading(false);
      }
    );

    // Fetch initial config if not provided
    if (!initialConfig) {
      IntentService.getIntentGroup(
        intentGroupId,
        false,
        setIsLoading,
        apiKey
      ).subscribe(
        (fetchedIntentGroup: IntentGroup) => {
          setConfig(fetchedIntentGroup.config);
          onChange(fetchedIntentGroup.config);
        },
        (error: any) => {
          console.error(error);
          setIsLoading(false);
        }
      );
    } else {
      setIsLoading(false);
    }
  }, [intentGroupId]);

  const updateConfig = (newConfig: Partial<Config>, loadingHandler: any, save: boolean = false) => {
    if (save) {
      setIsSaving(true);
      setConfig((prevConfig) => {
        if (!prevConfig) return newConfig;
        const updatedConfig = { ...prevConfig, ...newConfig };
        onChange(updatedConfig);
        loadingHandler(false);
        setIsSaving(false);
        setHasUnsavedChanges(false);
        return updatedConfig;
      });
    } else {
      setConfig((prevConfig) => {
        if (!prevConfig) return newConfig;
        const updatedConfig = { ...prevConfig, ...newConfig };
        onChange(updatedConfig);
        loadingHandler(false);
        return updatedConfig;
      });
    }
  };

  const resetConfig = () => {
    if (initialConfig) {
      setConfig(initialConfig);
      onChange(initialConfig);
      setHasUnsavedChanges(false);
    }
  };

  const handleSaveStateChange = (hasChanges: boolean, saving: boolean) => {
    setHasUnsavedChanges(hasChanges);
    setIsSaving(saving);
  };

  const handleEndpointChange = (value: string) => {
    setSelectedEndpoint(value);
    onEndpointChange(value);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card className="border-border">
          <CardHeader>
            <div className="space-y-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-96" />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-10 w-48" />
            </div>
          </CardContent>
        </Card>
        
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="border-border">
            <CardHeader>
              <Skeleton className="h-5 w-40" />
            </CardHeader>
            <CardContent className="space-y-4">
              {[...Array(3)].map((_, j) => (
                <div key={j} className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header Card */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <SettingsIcon className="w-5 h-5 text-maitai-green" />
            Test Run Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground font-gc-reg mb-6">
            Configure the settings for this test run
          </p>
          
          <div className="space-y-3 max-w-xs">
            <Label className="text-sm font-gc-med text-foreground">Region</Label>
            <Select value={selectedEndpoint} onValueChange={handleEndpointChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select region" />
              </SelectTrigger>
              <SelectContent>
                {endpoints.map((endpoint) => (
                  <SelectItem key={endpoint.address} value={endpoint.address || ""}>
                    {endpoint.default ? `${endpoint.region} (default)` : endpoint.region}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Configuration Panel */}
      <ConfigurationPanel
        intentGroupId={intentGroupId ? Number(intentGroupId) : null}
        config={config}
        updateConfig={updateConfig}
        resetConfig={resetConfig}
        allowSecondaryModel={false}
        onSaveStateChange={handleSaveStateChange}
      />
    </div>
  );
};

export default TestRunConfig;
